import { useCallback, useContext, useEffect, useState, type ReactNode } from 'react';
import { Link, Navigate, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from '../i18n/useTranslation';
import styles from './IcpAnalysisPage.module.css';
import { Alert, Button, Card, EmptyState, Heading, Stack, Text } from '../components/atoms';
import { AppLayout } from '../components/templates/AppLayout/AppLayout';
import { Header } from '../components/layout/Header';
import { Sidebar } from '../components/Sidebar';
import { PageContent } from '../components/templates/PageContent/PageContent';
import { PageHeader } from '../components/templates/PageHeader/PageHeader';
import { Section } from '../components/templates/Section/Section';
import { LiveRegion } from '../components/atoms/LiveRegion/LiveRegion';
import { LoadErrorState } from '../components/feedback/LoadErrorState/LoadErrorState';
import { ConfirmDialog } from '../components/ui/ConfirmDialog/ConfirmDialog';
import { ActiveAquariumContext } from '../contexts/AquariumContext';
import { useToast } from '../stores/toastStore';
import {
  ICP_PAGE_SIZE,
  deleteAnalysis,
  getAnalysis,
  listAnalyses,
  type IcpAnalysis,
} from '../services/icpAnalysisService';
import { ICP_ANALYSIS_PARAM, icpAnalysesPath, readIcpImportState } from '../lib/icpRoutes';
import { IcpImportNotice } from '../components/icp/IcpImportNotice/IcpImportNotice';
import { icpCaveatElements } from '../lib/icpCaveats';

/**
 * Query keys. All share the `['icp-analyses', aquariumId]` prefix, so invalidating
 * that prefix (after an upload, a delete or a Fauna Marin sync) refreshes the list
 * and the detail. ICP values are mirrored into the measurements, so changes also
 * invalidate `['measurements', aquariumId]` (latest values, dashboard tiles).
 */
const listKey = (aquariumId: string) => ['icp-analyses', aquariumId, 'list'] as const;
const detailKey = (aquariumId: string, analysisId: string) =>
  ['icp-analyses', aquariumId, 'detail', analysisId] as const;

const HISTORY_HEADING_CLASS = 'icp-history-heading';

function formatReportDate(analysis: Pick<IcpAnalysis, 'reportDate' | 'createdAt'>, locale: string | undefined): string {
  for (const value of [analysis.reportDate, analysis.createdAt]) {
    if (!value) continue;
    const date = new Date(value);
    if (!Number.isNaN(date.getTime())) {
      // Report dates are calendar dates (stored as midnight UTC): format in UTC so
      // they never shift by a day in negative-offset time zones.
      return date.toLocaleDateString(locale, { year: 'numeric', month: 'short', day: 'numeric', timeZone: 'UTC' });
    }
  }
  return '—';
}

function formatValue(value: number | string | null, locale: string | undefined): string {
  if (value === null || value === '') return '—';
  const numeric = typeof value === 'number' ? value : Number(value);
  return Number.isFinite(numeric) ? numeric.toLocaleString(locale, { maximumFractionDigits: 4 }) : String(value);
}

function AnalysisDetail({
  analysis,
  locale,
  onDelete,
  deleteFailed,
}: {
  analysis: IcpAnalysis;
  locale: string | undefined;
  onDelete: () => void;
  deleteFailed: boolean;
}) {
  const { t } = useTranslation();
  const entries = Object.entries(analysis.results);
  // #1829: quickly consumed elements can read low even when dosed.
  const caveats = icpCaveatElements(entries.map(([key]) => key));

  return (
    <Card padding="lg" className={styles.detail} data-testid="icp-analysis-detail">
      <Stack gap="6">
        <div className={styles.detailHead}>
          <Heading level={2} className={styles.detailTitle}>
            {analysis.provider || t('icp.analysisDetail')}
          </Heading>
          <Button variant="danger" size="sm" onClick={onDelete}>
            {t('icp.delete')}
          </Button>
        </div>

        {deleteFailed && <Alert compact>{t('icp.deleteModal.error')}</Alert>}

        <dl className={styles.meta}>
          <div className={styles.metaItem}>
            <dt>{t('icp.reportDate')}</dt>
            <dd>{formatReportDate(analysis, locale)}</dd>
          </div>
          <div className={styles.metaItem}>
            <dt>{t('icp.provider')}</dt>
            <dd>{analysis.provider || '—'}</dd>
          </div>
          {analysis.sampleId ? (
            <div className={styles.metaItem}>
              <dt>{t('icp.sampleId')}</dt>
              <dd>{analysis.sampleId}</dd>
            </div>
          ) : null}
          <div className={styles.metaItem}>
            <dt>{t('icp.parameters')}</dt>
            <dd>{analysis.parameterCount}</dd>
          </div>
        </dl>

        <Stack gap="3">
          <Heading level={3} className={styles.tableTitle}>
            {t('icp.parameters')}
          </Heading>
          {/* #1829: an ICP is a snapshot — say so before anyone corrects from it. */}
          <Alert tone="info" compact live="off" data-testid="icp-snapshot-note">
            {t('icp.context.snapshot')}
          </Alert>
          {entries.length === 0 ? (
            <Text variant="muted">{t('icp.noResults')}</Text>
          ) : (
            <div className={styles.tableWrap}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th scope="col">{t('icp.parameter')}</th>
                    <th scope="col" className={styles.numeric}>
                      {t('icp.value')}
                    </th>
                    <th scope="col">{t('icp.unit')}</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map(([key, value]) => (
                    <tr key={key}>
                      <th scope="row">{key}</th>
                      <td className={styles.numeric}>{formatValue(value, locale)}</td>
                      {/* No known unit (e.g. pH) → empty cell, never a guessed unit. */}
                      <td className={styles.unit}>{value === null || value === '' ? '' : (analysis.units[key] ?? '')}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {caveats.length > 0 && (
            <ul className={styles.caveats} data-testid="icp-element-caveats">
              {caveats.map((element) => (
                <li key={element}>
                  <Text size="sm" color="secondary">{String(t(`icp.context.caveat.${element}`))}</Text>
                </li>
              ))}
            </ul>
          )}
        </Stack>
      </Stack>
    </Card>
  );
}

export function IcpAnalysisPage() {
  const { t, i18n } = useTranslation();
  const locale = i18n?.language;
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const { aquariumId: routeAquariumId } = useParams<{ aquariumId: string }>();
  const activeAquariumId = useContext(ActiveAquariumContext)?.activeAquariumId ?? null;
  const aquariumId = routeAquariumId ?? null;
  const selectedId = searchParams.get(ICP_ANALYSIS_PARAM);
  // One-time notice after an upload (router state from IcpUploadPage).
  const importNotice = readIcpImportState(location.state);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteFailed, setDeleteFailed] = useState(false);
  const [focusHistory, setFocusHistory] = useState(false);

  const list = useInfiniteQuery({
    queryKey: listKey(aquariumId ?? ''),
    queryFn: ({ pageParam }) => listAnalyses(aquariumId ?? '', { limit: ICP_PAGE_SIZE, offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last) => {
      const next = last.offset + last.analyses.length;
      return last.analyses.length > 0 && next < last.total ? next : undefined;
    },
    enabled: aquariumId != null,
  });

  const detail = useQuery({
    queryKey: detailKey(aquariumId ?? '', selectedId ?? ''),
    queryFn: () => getAnalysis(aquariumId ?? '', selectedId ?? ''),
    enabled: aquariumId != null && selectedId != null,
  });

  const removeMutation = useMutation({
    mutationFn: (analysisId: string) => deleteAnalysis(aquariumId ?? '', analysisId),
    onSuccess: (_data, analysisId) => {
      setConfirmOpen(false);
      queryClient.removeQueries({ queryKey: detailKey(aquariumId ?? '', analysisId) });
      void queryClient.invalidateQueries({ queryKey: ['icp-analyses', aquariumId] });
      // Its values leave the latest values (dashboard tiles) too.
      void queryClient.invalidateQueries({ queryKey: ['measurements', aquariumId] });
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete(ICP_ANALYSIS_PARAM);
        return next;
      }, { replace: true });
      setFocusHistory(true);
      showToast(t('icp.deleteSuccess'), 'success');
    },
    onError: () => {
      setConfirmOpen(false);
      setDeleteFailed(true);
    },
  });

  useEffect(() => {
    setDeleteFailed(false);
  }, [selectedId]);

  // The deleted analysis' detail panel (and its Delete button) is gone: move focus to the history heading
  // (after ConfirmDialog's own focus restore has run).
  useEffect(() => {
    if (!focusHistory) return;
    setFocusHistory(false);
    document.querySelector<HTMLElement>(`.${HISTORY_HEADING_CLASS}`)?.focus();
  }, [focusHistory]);

  const handleCancelDelete = useCallback(() => setConfirmOpen(false), []);

  // /icp-analysis (sidebar, bookmarks): go to the canonical, deep-linkable URL.
  if (!routeAquariumId && activeAquariumId) {
    return <Navigate to={`${icpAnalysesPath(activeAquariumId)}${location.search}`} replace />;
  }

  const handleUpload = (): void => {
    if (aquariumId) navigate(`/aquariums/${aquariumId}/icp/upload`);
  };

  const analyses = list.data?.pages.flatMap((page) => page.analyses) ?? [];
  const total = list.data?.pages[list.data.pages.length - 1]?.total ?? analyses.length;

  let content: ReactNode;
  if (aquariumId == null) {
    content = (
      <EmptyState
        icon={<span>🐠</span>}
        headline={t('icp.noAquariumTitle')}
        description={t('icp.noAquariumDescription')}
        actionLabel={t('icp.noAquariumAction')}
        onAction={() => navigate('/aquariums')}
      />
    );
  } else if (list.isPending) {
    content = (
      <LiveRegion className={styles.loadingState}>
        <Text>{t('icp.loading')}</Text>
      </LiveRegion>
    );
  } else if (list.isError) {
    // Never the raw backend message — always the translated copy.
    content = <LoadErrorState title={t('icp.loadError')} titleLevel={2} onRetry={() => void list.refetch()} />;
  } else if (analyses.length === 0) {
    content = (
      <EmptyState
        icon={<span>📊</span>}
        headline={t('icp.emptyHeadline')}
        description={t('icp.emptyDescription')}
        actionLabel={t('icp.emptyAction')}
        actionVariant="secondary"
        onAction={handleUpload}
      />
    );
  } else {
    let detailContent: ReactNode;
    if (!selectedId) {
      detailContent = (
        <Card padding="lg" className={styles.selectPrompt}>
          <Text variant="muted">{t('icp.selectAnalysis')}</Text>
        </Card>
      );
    } else if (detail.isPending) {
      detailContent = (
        <LiveRegion className={styles.loadingState}>
          <Text>{t('icp.loadingDetail')}</Text>
        </LiveRegion>
      );
    } else if (detail.isError) {
      detailContent = (
        <LoadErrorState title={t('icp.detailLoadError')} titleLevel={2} onRetry={() => void detail.refetch()} />
      );
    } else {
      detailContent = (
        <AnalysisDetail
          analysis={detail.data}
          locale={locale}
          deleteFailed={deleteFailed}
          onDelete={() => {
            setDeleteFailed(false);
            setConfirmOpen(true);
          }}
        />
      );
    }

    const showImportNotice = importNotice != null && importNotice.analysisId === selectedId;
    const dismissImportNotice = (): void => {
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    };

    content = (
      <div className={styles.layout}>
        <Section
          title={t('icp.history')}
          headingProps={{ tabIndex: -1, className: `${HISTORY_HEADING_CLASS} ${styles.historyHeading ?? ''}` }}
          className={styles.history}
        >
          <Stack gap="3">
            <Text variant="muted">{t('icp.showing', { shown: analyses.length, total })}</Text>
            <ul className={styles.list}>
              {analyses.map((analysis) => {
                const selected = analysis.id === selectedId;
                return (
                  <li key={analysis.id}>
                    <Link
                      to={icpAnalysesPath(aquariumId, analysis.id)}
                      className={`${styles.item ?? ''} ${selected ? (styles.itemSelected ?? '') : ''}`}
                      aria-current={selected ? 'true' : undefined}
                    >
                      <span className={styles.itemDate}>{formatReportDate(analysis, locale)}</span>
                      <span className={styles.itemMeta}>
                        <span className={styles.itemProvider}>{analysis.provider || '—'}</span>
                        <span>{t('icp.parametersCount', { count: analysis.parameterCount })}</span>
                      </span>
                    </Link>
                  </li>
                );
              })}
            </ul>
            {list.isFetchNextPageError && <Alert compact>{t('icp.loadMoreError')}</Alert>}
            {list.hasNextPage && (
              <Button
                variant="secondary"
                fullWidth
                isLoading={list.isFetchingNextPage}
                onClick={() => void list.fetchNextPage()}
              >
                {t('icp.loadMore')}
              </Button>
            )}
          </Stack>
        </Section>

        <div className={styles.detailColumn}>
          {showImportNotice ? (
            <Stack gap="4">
              <IcpImportNotice
                parameterCount={importNotice.parameterCount}
                belowDetection={importNotice.belowDetection}
                onDismiss={dismissImportNotice}
              />
              {detailContent}
            </Stack>
          ) : (
            detailContent
          )}
        </div>
      </div>
    );
  }

  return (
    <AppLayout
      header={<Header />}
      sidebar={<Sidebar />}
      main={
        <PageContent width="wide">
          <PageHeader
            title={t('icp.pageTitle')}
            description={t('icp.pageDescription')}
            actions={
              aquariumId != null ? (
                <Button variant="primary" onClick={handleUpload}>
                  {t('icp.uploadNew')}
                </Button>
              ) : undefined
            }
          />
          {content}
          <ConfirmDialog
            isOpen={confirmOpen}
            title={t('icp.deleteModal.title')}
            message={t('icp.confirmDelete')}
            confirmLabel={t('icp.deleteModal.confirm')}
            cancelLabel={t('common.cancel')}
            isConfirming={removeMutation.isPending}
            onConfirm={() => {
              if (selectedId && !removeMutation.isPending) removeMutation.mutate(selectedId);
            }}
            onCancel={handleCancelDelete}
          />
        </PageContent>
      }
    />
  );
}

export default IcpAnalysisPage;
